import { useState, useMemo, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styled from "styled-components";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/FormRadix";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/Card";
import { CloseIcon } from "components/@icons/CloseIcon";

import {
  BepSalesFormSchema,
  type BepSalesFormValues,
  type SalesCategory,
} from "./validators/bep-sales-schema";

const CategoryTabBar = styled.div`
  padding: 4px;
  background: #e9eefb;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CategoryTabButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  padding: 0;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  border-radius: 8px;
  transition:
    background 0.15s,
    color 0.15s,
    box-shadow 0.15s;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? "#4B4F58" : "#6C727F")};
  box-shadow: ${({ $active }) =>
    $active ? "0 1px 2px 0 rgba(16,30,54,0.05)" : "none"};

  &:hover {
    background: ${({ $active }) => ($active ? "#fff" : "#f3f4f6")};
  }
`;

const StyledFormItem = styled(FormItem)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  text-align: right;
  border-radius: 8px;
  border: 1px solid #e3e5e8;
  padding: 0.375rem 0.5rem;
  font-size: 14px;
  width: 100%;
  outline: none;

  &:focus {
    border-color: #1f6aff;
    box-shadow: 0 0 0 1px #1f6aff33;
  }
`;

const UnitText = styled.span`
  margin-left: 0.5rem;
  color: #6c727f;
  font-size: 0.875rem;
`;

const FooterButton = styled.button<{ $primary?: boolean }>`
  border-radius: 6px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-size: 14px;
  font-weight: ${({ $primary }) => ($primary ? 600 : 500)};
  background: ${({ $primary }) => ($primary ? "#1F6AFF" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#1F6AFF")};
  border: ${({ $primary }) => ($primary ? "none" : "1px solid #e3e5e8")};
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: ${({ $primary }) => ($primary ? "#2563eb" : "#f3f4f6")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type BepSalesFormProps = {
  initialData?: BepSalesFormValues;
  isSubmitting: boolean;
  onSubmit: (values: BepSalesFormValues) => void;
  onCancel: () => void;
};

export function BepSalesForm({
  initialData,
  isSubmitting,
  onSubmit,
  onCancel,
}: BepSalesFormProps) {
  const [activeCategory, setActiveCategory] = useState<SalesCategory>("전체");

  const form = useForm<BepSalesFormValues>({
    resolver: zodResolver(BepSalesFormSchema),
    defaultValues: initialData || { items: [] },
    disabled: isSubmitting,
  });

  const watchedItems = useWatch({ control: form.control, name: "items" });

  const activeIndex = useMemo(() => {
    return (
      watchedItems?.findIndex((item) => item.category === activeCategory) ?? -1
    );
  }, [activeCategory, watchedItems]);

  const formItems = watchedItems || [];

  useEffect(() => {
    const allCategories: SalesCategory[] =
      BepSalesFormSchema.shape.items.element.shape.category._def.values;

    if (!watchedItems || watchedItems.length < allCategories.length) {
      const defaultItems = allCategories.map((cat) => {
        const existingItem = watchedItems?.find(
          (item) => item.category === cat
        );

        return {
          category: cat,
          bep: existingItem?.bep ?? null,
          targetSales: existingItem?.targetSales ?? null,
        };
      });

      form.setValue("items", defaultItems, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    if (activeIndex === -1 && watchedItems && watchedItems.length > 0) {
      setActiveCategory(watchedItems[0].category);
    }
  }, [watchedItems, form, activeIndex]);

  return (
    <Card
      style={{
        width: "100%",
        border: 0,
        gap: "16px",
        padding: 0,
        boxShadow: "none",
      }}
    >
      <CardHeader style={{ gap: "20px", padding: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CardTitle
            style={{
              fontSize: 20,
              fontWeight: "bold",
              lineHeight: "28px",
              letterSpacing: "-0.4px",
            }}
          >
            BEP · 목표 매출 설정
          </CardTitle>
          <CloseIcon onClick={onCancel} />
        </div>
        <CardDescription
          style={{
            color: "#6B748B",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "24px",
            letterSpacing: "-0.2px",
          }}
        >
          BEP와 목표 매출을 기준으로 실적을 분석하고 그래프에 반영합니다.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <CardContent
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: 0,
              boxShadow: "none",
              border: "none",
            }}
          >
            <CategoryTabBar>
              {formItems.map((item) => (
                <CategoryTabButton
                  type="button"
                  key={item.category}
                  $active={activeCategory === item.category}
                  onClick={() => setActiveCategory(item.category)}
                  style={{ outline: "none", border: "none" }}
                >
                  {item.category}
                </CategoryTabButton>
              ))}
            </CategoryTabBar>

            {activeIndex !== -1 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* BEP 필드 */}
                <FormField
                  key={`${activeCategory}-bep`}
                  control={form.control}
                  name={`items.${activeIndex}.bep`}
                  render={({ field }) => (
                    <StyledFormItem>
                      <FormLabel
                        style={{
                          minWidth: 120,
                          color: "#6C727F",
                        }}
                      >
                        BEP (손익분기점)
                      </FormLabel>
                      <InputWrap>
                        <FormControl>
                          <StyledInput
                            placeholder="BEP 입력"
                            {...field}
                            value={
                              typeof field.value === "number"
                                ? field.value.toLocaleString("ko-KR")
                                : ""
                            }
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/,/g, "");
                              field.onChange(
                                rawValue === "" ? null : Number(rawValue)
                              );
                            }}
                          />
                        </FormControl>
                        <UnitText>원</UnitText>
                      </InputWrap>
                      <FormMessage />
                    </StyledFormItem>
                  )}
                />

                {/* 목표 매출 필드 */}
                <FormField
                  key={`${activeCategory}-targetSales`}
                  control={form.control}
                  name={`items.${activeIndex}.targetSales`}
                  render={({ field }) => (
                    <StyledFormItem>
                      <FormLabel
                        style={{
                          minWidth: 120,
                          color: "#6C727F",
                        }}
                      >
                        목표 매출
                      </FormLabel>
                      <InputWrap>
                        <FormControl>
                          <StyledInput
                            placeholder="목표 매출 입력"
                            {...field}
                            value={
                              typeof field.value === "number"
                                ? field.value.toLocaleString("ko-KR")
                                : ""
                            }
                            onChange={(e) => {
                              const rawValue = e.target.value.replace(/,/g, "");
                              field.onChange(
                                rawValue === "" ? null : Number(rawValue)
                              );
                            }}
                          />
                        </FormControl>
                        <UnitText>원</UnitText>
                      </InputWrap>
                      <FormMessage />
                    </StyledFormItem>
                  )}
                />
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  padding: "1rem 0",
                  fontSize: "14px",
                }}
              >
                카테고리를 선택하거나 초기 데이터를 불러오는 중입니다.
              </div>
            )}
          </CardContent>
          <CardFooter
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "16px",
              padding: 0,
              border: 0,
              borderBottom: 0,
              boxShadow: "none",
            }}
          >
            <FooterButton
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              취소
            </FooterButton>
            <FooterButton
              type="button"
              $primary
              disabled={isSubmitting}
              onClick={form.handleSubmit(onSubmit, (error) =>
                console.log(error)
              )}
            >
              {isSubmitting ? "설정 중..." : "설정하기"}
            </FooterButton>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
